"use client";

import { createContext, useContext } from "react";

/**
 * Publishes the active locale's href localizer to app-agnostic components.
 *
 * `createLocaleProvider` builds a fresh context per call, so each app's prefix
 * context is private to that app and unreachable from shared packages. This
 * context is module-level and carries the already-bound localizer instead of
 * the prefix, so a consumer in `packages/ui` needs no knowledge of which app it
 * is rendering in, nor of that app's route tables.
 *
 * The default is identity, so components render unchanged in apps that mount no
 * provider (pioneer, publisher, signup, Storybook) and on routes that sit
 * outside the `[locale]` segment.
 */
const LocalizeHrefContext = createContext<(href: string) => string>(
  (href) => href,
);

export function LocalizeHrefProvider({
  localizeHref,
  children,
}: {
  localizeHref: (href: string) => string;
  children: React.ReactNode;
}) {
  return (
    <LocalizeHrefContext.Provider value={localizeHref}>
      {children}
    </LocalizeHrefContext.Provider>
  );
}

/**
 * Returns the active locale's href localizer. Safe on any href — external
 * URLs, anchors, and routes the app does not serve under `[locale]` come back
 * unchanged, so call sites need no per-link judgement.
 */
export function useLocalizeHref(): (href: string) => string {
  return useContext(LocalizeHrefContext);
}
